import { inject, Injectable } from '@angular/core';
import { GoogleAnalyticsService } from '@interacta-shared/data-access-common';
import { ErrorService } from '@interacta-shared/data-access-error';
import { filterMap, Index, uuid } from '@interacta-shared/util';
import { getRemainingDaysInMonth } from '@interacta-shared/util-common';
import {
    AICreditActions,
    isCreditLimitExceededError,
} from '@interacta/data-access-ai';
import { AIConvoThinkingEvent } from '@modules/ai-convo/models/ai-convo-thinking.model';
import { getAIErrorMessage } from '@modules/ai-shared/helpers/ai-shared.utils';
import { AISharedMessageType } from '@modules/ai-shared/models/ai-shared.model';
import { AISharedService } from '@modules/ai-shared/services/ai-shared.service';
import { AISharedActions } from '@modules/ai-shared/store/ai-shared.actions';
import { selectAIIsSidePanelOpen } from '@modules/ai-shared/store/ai-shared.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
    catchError,
    concatMap,
    filter,
    map,
    mergeMap,
    Observable,
    of,
    switchMap,
    tap,
    TimeoutError,
} from 'rxjs';
import { AIConvoThinkingChannelService } from '../services/ai-convo-thinking-channel.service';
import { AIConvoService } from '../services/ai-convo.service';
import { AIConvoActions } from './ai-convo.actions';
import { AIConvoAPIActions } from './ai-convo.api-actions';
import {
    selectActiveAIConvoChat,
    selectAIChatById,
} from './ai-convo.selectors';

@Injectable()
export class AIConvoEffects {
    private readonly actions = inject(Actions);
    private readonly aiConvoService = inject(AIConvoService);
    private readonly thinkingChannel = inject(AIConvoThinkingChannelService);
    private readonly aiSharedService = inject(AISharedService);
    private readonly errorService = inject(ErrorService);
    private readonly store = inject(Store);
    private readonly translateService = inject(TranslateService);
    private readonly gaService = inject(GoogleAnalyticsService);

    askAboutAttachment$ = createEffect(() =>
        this.actions.pipe(
            ofType(AIConvoActions.askAboutAttachment),
            concatLatestFrom(() => this.store.select(selectAIIsSidePanelOpen)),
            concatMap(([{ attachment }, isPanelOpen]) => {
                if (!isPanelOpen) {
                    this.gaService.sendCustomEvent(
                        'isp_ai_conversational_opened',
                        { source: 'attachment' },
                    );
                }
                return [
                    ...(isPanelOpen
                        ? []
                        : [
                              AISharedActions.setIsPanelOpen({ isOpen: true }),
                              AIConvoActions.startNewChat({
                                  newChatId: uuid(),
                              }),
                          ]),
                    AIConvoActions.setPendingAttachment({
                        pending: { attachment, source: 'action' },
                    }),
                ];
            }),
        ),
    );

    trackAnswerReceived$ = createEffect(
        () =>
            this.actions.pipe(
                ofType(AIConvoAPIActions.fetchResponseFromAISuccess),
                tap(({ references }) =>
                    this.gaService.sendCustomEvent(
                        'isp_ai_conversational_answer_received',
                        { has_sources: references.length > 0 },
                    ),
                ),
            ),
        { dispatch: false },
    );

    trackHistoryConversationOpened$ = createEffect(
        () =>
            this.actions.pipe(
                ofType(AIConvoActions.resumeChat),
                tap(() =>
                    this.gaService.sendCustomEvent(
                        'isp_ai_history_conversation_opened',
                    ),
                ),
            ),
        { dispatch: false },
    );

    getResponseFromAI$ = createEffect(() =>
        this.actions.pipe(
            ofType(AIConvoActions.fetchResponseFromAI),
            concatLatestFrom(({ chatId }) =>
                this.store.select(selectAIChatById(chatId)),
            ),
            filterMap(
                ([
                    {
                        chatId,
                        sessionId,
                        message,
                        communityFilter,
                        context,
                        attachment,
                        reference,
                        smartActionId,
                    },
                    chatEntry,
                ]) =>
                    chatEntry
                        ? {
                              chatId,
                              sessionId,
                              historyId: chatEntry.historyId,
                              knownMessageIds: chatEntry.messages.map(
                                  (m) => m.id,
                              ),
                              message,
                              communityFilter,
                              context,
                              attachmentId: attachment?.id ?? null,
                              referenceId: reference?.id ?? null,
                              smartActionId,
                              agentId: chatEntry.activeAgent?.id ?? null,
                          }
                        : undefined,
            ),
            concatMap(
                ({
                    chatId,
                    sessionId,
                    historyId,
                    knownMessageIds,
                    message,
                    communityFilter,
                    context,
                    attachmentId,
                    referenceId,
                    smartActionId,
                    agentId,
                }) => {
                    // The ask route has no agent field: a chat with an agent still waits for the synchronous answer.
                    if (agentId !== null)
                        return this.aiConvoService
                            .fetchResponseFromAIConvo(
                                agentId,
                                communityFilter,
                                message,
                                sessionId,
                                context,
                                attachmentId,
                                referenceId,
                                smartActionId,
                            )
                            .pipe(
                                map(({ sessionId, answer: response, agent }) =>
                                    AIConvoAPIActions.fetchResponseFromAISuccess(
                                        {
                                            chatId,
                                            sessionId,
                                            message: response.content,
                                            messageDelta:
                                                response.messageDelta ?? null,
                                            messageId: response.id,
                                            timestamp: response.timestamp,
                                            references: response.references,
                                            deltaReferences:
                                                response.deltaReferences ?? [],
                                            agent,
                                        },
                                    ),
                                ),
                                catchError((error) =>
                                    this.toErrorActions(chatId, error),
                                ),
                            );

                    return this.aiConvoService
                        .askQuestion(
                            communityFilter,
                            message,
                            historyId,
                            context,
                            attachmentId,
                            referenceId,
                            smartActionId,
                        )
                        .pipe(
                            concatMap((channel) =>
                                this.thinkingChannel.listen(channel),
                            ),
                            concatMap((event) =>
                                this.channelEventToActions(
                                    chatId,
                                    sessionId,
                                    event,
                                ),
                            ),
                            catchError((error) =>
                                error instanceof TimeoutError &&
                                historyId !== null
                                    ? this.recoverAnswer(
                                          chatId,
                                          sessionId,
                                          historyId,
                                          knownMessageIds,
                                          error,
                                      )
                                    : this.toErrorActions(chatId, error),
                            ),
                        );
                },
            ),
        ),
    );

    /** The channel stayed silent: the backend may still have saved the answer, which the history then holds. */
    private recoverAnswer(
        chatId: string,
        sessionId: string | null,
        historyId: number,
        knownMessageIds: Index[],
        timeoutError: TimeoutError,
    ): Observable<Action> {
        return this.aiConvoService
            .recoverLatestAnswer(historyId, knownMessageIds)
            .pipe(
                mergeMap((message) =>
                    message
                        ? this.channelEventToActions(chatId, sessionId, {
                              type: 'result',
                              response: { sessionId: null, answer: message },
                              historyId,
                          })
                        : this.toErrorActions(chatId, timeoutError),
                ),
                catchError((error) => this.toErrorActions(chatId, error)),
            );
    }

    private toErrorActions(chatId: string, error: unknown): Observable<Action> {
        if (isCreditLimitExceededError(error)) {
            const remainingDays = getRemainingDaysInMonth();
            const creditLimitKey =
                remainingDays === 1
                    ? 'AI.SIDE_PANEL.CREDIT_LIMIT_INLINE_ERROR_SING'
                    : 'AI.SIDE_PANEL.CREDIT_LIMIT_INLINE_ERROR_PLUR';
            return of(
                AICreditActions.creditLimitExceededDetected(),
                AIConvoAPIActions.fetchResponseFromAIError({
                    chatId,
                    message: this.translate(creditLimitKey, {
                        count: remainingDays,
                    }),
                    messageId: uuid(),
                    timestamp: new Date(),
                    messageType: AISharedMessageType.AI_CREDIT_LIMIT_EXCEEDED,
                }),
            );
        }

        const { messageType, message } = getAIErrorMessage(error);
        return of(
            AIConvoAPIActions.fetchResponseFromAIError({
                chatId,
                message: this.translate(message),
                messageId: uuid(),
                timestamp: new Date(),
                messageType,
            }),
        );
    }

    private translate(key: string, params?: object): string {
        return this.translateService.instant(key, params) as string;
    }

    private channelEventToActions(
        chatId: string,
        sessionId: string | null,
        event: AIConvoThinkingEvent,
    ): Action[] {
        switch (event.type) {
            case 'result': {
                const { answer, agent } = event.response;
                return [
                    AIConvoAPIActions.fetchResponseFromAISuccess({
                        chatId,
                        sessionId,
                        historyId: event.historyId,
                        message: answer.content,
                        messageDelta: answer.messageDelta ?? null,
                        messageId: answer.id,
                        timestamp: answer.timestamp,
                        references: answer.references,
                        deltaReferences: answer.deltaReferences ?? [],
                        agent,
                    }),
                ];
            }
            case 'thinking':
                return [
                    AIConvoAPIActions.thinkingStepReceived({
                        chatId,
                        step: event.step,
                    }),
                ];
            case 'conversation_title':
                return [
                    AIConvoAPIActions.conversationTitleReceived({
                        chatId,
                        title: event.title,
                    }),
                ];
            default:
                return [
                    AIConvoAPIActions.fetchResponseFromAIError({
                        chatId,
                        message: this.translate('AI.ERROR'),
                        messageId: uuid(),
                        timestamp: new Date(),
                        messageType: AISharedMessageType.GENERIC_ERROR,
                    }),
                ];
        }
    }

    sendFeedbackToAI$ = createEffect(() =>
        this.actions.pipe(
            ofType(AIConvoActions.sendFeedback),
            concatMap(({ messageId, like, chatId }) =>
                this.aiSharedService
                    .sendFeedbackToAI(messageId, like, undefined, undefined)
                    .pipe(
                        map(() =>
                            AIConvoAPIActions.sendFeedbackSuccess({
                                messageId,
                                like,
                                chatId,
                            }),
                        ),
                        catchError((error) =>
                            of(
                                AIConvoAPIActions.sendFeedbackError({
                                    error,
                                    messageId,
                                    chatId,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    sendFeedbackDetailToAI$ = createEffect(() =>
        this.actions.pipe(
            ofType(AIConvoActions.sendFeedbackDetail),
            concatMap(({ messageId, like, feedbacks, text, chatId }) =>
                this.aiSharedService
                    .sendFeedbackToAI(messageId, like, feedbacks, text)
                    .pipe(
                        map(() =>
                            AIConvoAPIActions.sendFeedbackDetailSuccess({
                                messageId,
                                like,
                                feedbacks,
                                text,
                                chatId,
                            }),
                        ),
                        catchError((error) =>
                            of(
                                AIConvoAPIActions.sendFeedbackDetailError({
                                    error,
                                    messageId,
                                    chatId,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    fetchAgents$ = createEffect(() =>
        this.actions.pipe(
            ofType(AIConvoActions.fetchAgents),
            switchMap(() =>
                this.aiConvoService.fetchAgents().pipe(
                    map((agents) =>
                        AIConvoAPIActions.fetchAgentsSuccess({ agents }),
                    ),
                    catchError((error) =>
                        of(AIConvoAPIActions.fetchAgentsError({ error })),
                    ),
                ),
            ),
        ),
    );

    fetchHistory$ = createEffect(() =>
        this.actions.pipe(
            ofType(AIConvoActions.fetchHistory),
            switchMap(({ pageToken }) =>
                this.aiConvoService.fetchHistory(pageToken).pipe(
                    map((history) =>
                        AIConvoAPIActions.fetchHistorySuccess({ history }),
                    ),
                    catchError((error) =>
                        of(AIConvoAPIActions.fetchHistoryError({ error })),
                    ),
                ),
            ),
        ),
    );

    creditLimitRestored$ = createEffect(() =>
        this.actions.pipe(
            ofType(AIConvoAPIActions.fetchResponseFromAISuccess),
            map(() => AICreditActions.creditLimitRestored()),
        ),
    );

    resumeChat$ = createEffect(() =>
        this.actions.pipe(
            ofType(AIConvoActions.resumeChat),
            switchMap(({ historyId, newChatId }) =>
                this.aiConvoService.fetchSessionMessages(historyId).pipe(
                    map((page) =>
                        AIConvoAPIActions.fetchSessionMessagesSuccess({
                            chatId: newChatId,
                            page,
                        }),
                    ),
                    catchError((error) =>
                        of(
                            AIConvoAPIActions.fetchSessionMessagesError({
                                chatId: newChatId,
                                error,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    fetchSessionMessages$ = createEffect(() =>
        this.actions.pipe(
            ofType(AIConvoActions.fetchSessionMessages),
            concatMap(({ chatId, historyId, pageToken }) =>
                this.aiConvoService
                    .fetchSessionMessages(historyId, pageToken)
                    .pipe(
                        map((page) =>
                            AIConvoAPIActions.fetchSessionMessagesSuccess({
                                chatId,
                                page,
                            }),
                        ),
                        catchError((error) =>
                            of(
                                AIConvoAPIActions.fetchSessionMessagesError({
                                    chatId,
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    renameSession$ = createEffect(() =>
        this.actions.pipe(
            ofType(AIConvoActions.renameSession),
            concatMap(({ historyId, title }) =>
                this.aiConvoService.renameSession(historyId, title).pipe(
                    map(() =>
                        AIConvoAPIActions.renameSessionSuccess({
                            historyId,
                            title,
                        }),
                    ),
                    catchError((error) =>
                        of(AIConvoAPIActions.renameSessionError({ error })),
                    ),
                ),
            ),
        ),
    );

    deleteSession$ = createEffect(() =>
        this.actions.pipe(
            ofType(AIConvoActions.deleteSession),
            concatMap(({ historyId, sessionId }) =>
                this.aiConvoService.deleteSession(historyId).pipe(
                    map(() =>
                        AIConvoAPIActions.deleteSessionSuccess({
                            historyId,
                            sessionId,
                        }),
                    ),
                    catchError((error) =>
                        of(AIConvoAPIActions.deleteSessionError({ error })),
                    ),
                ),
            ),
        ),
    );

    startNewChatOnActiveSessionDeleted$ = createEffect(() =>
        this.actions.pipe(
            ofType(AIConvoAPIActions.deleteSessionSuccess),
            concatLatestFrom(() => this.store.select(selectActiveAIConvoChat)),
            filter(
                ([{ historyId, sessionId }, activeChat]) =>
                    activeChat?.historyId === historyId ||
                    activeChat?.sessionId === sessionId,
            ),
            map(() => AIConvoActions.startNewChat({ newChatId: uuid() })),
        ),
    );

    notifyError$ = createEffect(
        () =>
            this.actions.pipe(
                ofType(
                    AIConvoAPIActions.renameSessionError,
                    AIConvoAPIActions.deleteSessionError,
                ),
                tap(({ error }) => this.errorService.handle(error)),
            ),
        { dispatch: false },
    );
}
